import { API_BASE_URL } from "./client";

export type BenefitDocument = {
  id: number;
  file_name: string;
  file_type: string;
  file_size: number;
  status: "pending" | "approved" | "rejected";
  uploaded_at: string;
  rejection_reason: string | null;
  reviewed_at: string | null;
};

export type AdminBenefitDocument = BenefitDocument & {
  tenant_id: number;
  tenant_name: string;
  tenant_email: string;
  reviewed_by_name: string | null;
};

export async function uploadBenefitDocument(
  token: string,
  file: File,
): Promise<BenefitDocument> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}/api/tenant/documents`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    let message = "Upload failed";
    try {
      const parsed = await response.json();
      message = parsed?.error || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  return response.json();
}

export async function getTenantDocuments(token: string) {
  const response = await fetch(`${API_BASE_URL}/api/tenant/documents`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch documents");
  }

  return response.json() as Promise<BenefitDocument[]>;
}

export async function deleteTenantDocument(token: string, documentId: number) {
  const response = await fetch(
    `${API_BASE_URL}/api/tenant/documents/${documentId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to delete document");
  }

  return response.json();
}

export async function getPendingDocuments(token: string) {
  const response = await fetch(`${API_BASE_URL}/api/admin/documents/pending`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch pending documents");
  }

  return response.json() as Promise<AdminBenefitDocument[]>;
}

export async function getPendingDocumentCount(token: string) {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/documents/pending/count`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch pending document count");
  }

  return response.json() as Promise<{ count: number }>;
}

export async function getAllDocuments(
  token: string,
  status?: string,
  tenantId?: number,
) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (tenantId) params.set("tenant_id", String(tenantId));
  const query = params.toString();
  const url = query
    ? `${API_BASE_URL}/api/admin/documents?${query}`
    : `${API_BASE_URL}/api/admin/documents`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch documents");
  }

  return response.json() as Promise<AdminBenefitDocument[]>;
}

export async function reviewDocument(
  token: string,
  documentId: number,
  status: "approved" | "rejected",
  rejectionReason?: string,
) {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/documents/${documentId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status, rejection_reason: rejectionReason }),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to review document");
  }

  return response.json();
}

export async function deleteAdminDocument(token: string, documentId: number) {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/documents/${documentId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to delete document");
  }

  return response.json();
}

export function getDocumentUrl(documentId: number) {
  return `${API_BASE_URL}/api/admin/documents/file/${documentId}`;
}
